import { Text } from "@src/lib/ui-react";
import { HCF_USER_EVENTS } from "src/constants/firebaseEvents";
import { activateUser, removeUser } from "src/containers/facilityUser/api";
import {
  LOADING_STATE,
  PROCESS_MODAL_STATE,
  ROLE_MAPPINGS,
  TAB_STATE,
} from "src/containers/teamMembers/constants";
import { generateLogData } from "src/containers/teamMembers/logs";
import { TeamMemberActions } from "src/containers/teamMembers/teamMemberReducer";
import { ITeamMember } from "src/containers/teamMembers/useTeamMembersAPIHook";
import { Button } from "src/designsystem/Button/Button";
import { SessionType } from "src/modules/interface";
import { getErrorMessage } from "src/utils/errors";
import { logEvent } from "@src/appV2/lib/analytics";
import { Dispatch, useState } from "react";
import { useSelector } from "react-redux";
import { Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { Close as CloseIcon } from "@mui/icons-material";

type OkProps = {
  dispatch: Dispatch<TeamMemberActions>;
  member: ITeamMember;
  admin: SessionType["session"]["admin"];
  adminUserId: SessionType["session"]["adminUserId"];
  type: SessionType["session"]["type"];
  workplaceId: SessionType["session"]["profile"]["userId"];
  user: SessionType["session"]["user"];
  userId: SessionType["session"]["userId"];
  getTeamMembers: (id: string, isArchived: boolean) => void;
};

const deactivate = async ({
  dispatch,
  member,
  admin,
  adminUserId,
  type,
  workplaceId,
  user,
  userId,
  getTeamMembers,
}: OkProps) => {
  if (!member) {
    return;
  }

  const event = generateLogData(userId, admin, adminUserId, type, workplaceId, member._id);
  try {
    await removeUser({
      userId: member._id,
      firebaseId: member.firebaseId,
      performedBy: admin ? adminUserId : user,
      facilityId: userId,
    });
    logEvent(HCF_USER_EVENTS.DEACTIVATE_TEAM_MEMBER, {
      ...event,
      success: true,
    });
    getTeamMembers(userId, false);
  } catch (error) {
    showErrorToast(getErrorMessage(error));
    logEvent(HCF_USER_EVENTS.DEACTIVATE_TEAM_MEMBER, {
      ...event,
      success: false,
    });
  } finally {
    dispatch({ type: "SET_MODAL_MEMBER", member: undefined });
    dispatch({ type: "CLEAR_TEAM_MEMBERS" });
  }
};

const activate = async ({
  dispatch,
  member,
  admin,
  adminUserId,
  type,
  workplaceId,
  user,
  userId,
  getTeamMembers,
}: OkProps) => {
  const event = generateLogData(userId, admin, adminUserId, type, workplaceId, member._id);
  try {
    await activateUser({
      userId: member._id,
      firebaseId: member.firebaseId,
      performedBy: admin ? adminUserId : user,
      facilityId: userId,
    });
    logEvent(HCF_USER_EVENTS.ACTIVATE_TEAM_MEMBER, {
      ...event,
      success: true,
    });
    getTeamMembers(userId, true);
  } catch (error) {
    showErrorToast(getErrorMessage(error));
    logEvent(HCF_USER_EVENTS.ACTIVATE_TEAM_MEMBER, {
      ...event,
      success: false,
    });
  } finally {
    dispatch({ type: "SET_MODAL_MEMBER", member: undefined });
    dispatch({ type: "CLEAR_TEAM_MEMBERS" });
  }
};

type TeamMemberStatusModalProps = {
  activeTab: TAB_STATE;
  member?: ITeamMember;
  dispatch: Dispatch<TeamMemberActions>;
  getTeamMembers: (id: string, isArchived: boolean) => void;
  visibility: boolean;
};

export function TeamMemberStatusModal({
  activeTab,
  member,
  dispatch,
  getTeamMembers,
  visibility,
}: TeamMemberStatusModalProps) {
  const {
    user,
    admin,
    adminUserId,
    userId,
    type,
    profile: { userId: workplaceId },
  } = useSelector((state: SessionType) => state.session);
  const [loading, setLoading] = useState<LOADING_STATE>();

  const handleOk = async () => {
    if (!member) {
      return;
    }

    setLoading(LOADING_STATE.ACTIVATE_OR_DEACTIVATE);
    if (activeTab === TAB_STATE.ACTIVE) {
      await deactivate({
        dispatch,
        member,
        admin,
        adminUserId,
        type,
        workplaceId,
        user,
        userId,
        getTeamMembers,
      });
    } else {
      await activate({
        dispatch,
        member,
        admin,
        adminUserId,
        type,
        workplaceId,
        user,
        userId,
        getTeamMembers,
      });
    }
    setLoading(undefined);
    dispatch({ type: "SET_STATUS_MODAL_VISIBILITY", statusModal: false });
    dispatch({
      type: "SET_PROCESS_MODAL_VISIBILITY",
      processModal: PROCESS_MODAL_STATE.off,
    });
  };

  const handleCancel = () => {
    dispatch({ type: "SET_STATUS_MODAL_VISIBILITY", statusModal: false });
  };

  if (!member) {
    return null;
  }

  const memberRoles = member.roles.map((role) => ROLE_MAPPINGS[role].label).join(", ");

  return (
    <Dialog
      open={visibility}
      maxWidth="md"
      disableEscapeKeyDown={loading === LOADING_STATE.ACTIVATE_OR_DEACTIVATE}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Text variant="h3">
            {activeTab === TAB_STATE.ACTIVE ? "Deactivate Account" : "Activate Account"}
          </Text>
          <CloseIcon aria-label="Close" onClick={handleCancel} />
        </Stack>
      </DialogTitle>
      <DialogContent dividers={true}>
        <Stack spacing={1}>
          {activeTab === TAB_STATE.INACTIVE ? (
            <div>
              Are you sure you want to activate{" "}
              <b>
                {member.name} (role: {memberRoles})
              </b>
              ?
            </div>
          ) : (
            <>
              <Text variant="body2">
                <b>
                  {member.name} (role: {memberRoles})
                </b>{" "}
                will lose access to all associated workplaces and will no longer be able to log in.
              </Text>
              <Text variant="body2"> Are you sure you want to deactivate?</Text>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ paddingX: 3, padding: 2 }}>
        <Button
          variant="tertiary"
          onClick={handleOk}
          isLoading={loading === LOADING_STATE.ACTIVATE_OR_DEACTIVATE}
          key={3}
        >
          {activeTab === TAB_STATE.INACTIVE ? "Activate" : "Deactivate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
